import { Box, Button, Grid, RadioGroup, Stack, Typography } from "@mui/material";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { DrawType } from "Forms/Schemas/Goals/otherGoalFormSchema";
import {
  useGetFamilyGoal,
  useGetFinancialReserveGoal,
  useGetHousingMortgageGoal,
  useGetHousingOtherGoal,
  useGetHousingPropertyGoal,
  useGetOtherGoal,
  useGetPensionGoal,
  useGetVehicleGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  type GoalOverview,
  GoalOverviewPayoutFrequency,
  GoalOverviewSubType,
  GoalOverviewType,
  OtherGoalDataPayoutFrequency,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useRef, useState } from "react";
import { ChildrenGoalPane } from "./ChildrenGoalPane";
import type { SubmitGoalFormHandle } from "./CreateGoalDrawer";
import { FinancialReserveForm } from "./FinancialReserveForm";
import { GoalTile } from "./GoalTile";
import { HousingMortgageGoalForm } from "./HousingMortgageGoalForm";
import { HousingOtherGoalForm } from "./HousingOtherGoalForm";
import { HousingPropertyGoalForm } from "./HousingPropertyGoalForm";
import { OtherGoalMonthlyForm } from "./OtherGoalMonthlyForm";
import { OtherGoalOneTimeForm } from "./OtherGoalOneTimeForm";
import { PensionGoalForm } from "./PensionGoalForm";
import { VehicleGoalForm } from "./VehicleGoalForm";

type Props = {
  readonly onClose: () => void;
  readonly goal: GoalOverview;
  readonly open: boolean;
  readonly onExited?: () => void;
};

export const EditGoalDrawer: FC<Props> = ({ onClose: propsOnClose, goal, open, onExited }) => {
  const { t } = useResource();
  const { analysisId, versionId, readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const [goalType, setGoalType] = useState<GoalOverviewType>(goal.type);
  const [goalSubType, setGoalSubType] = useState<GoalOverviewSubType>(
    goal.subType === GoalOverviewSubType.none ? GoalOverviewSubType.housing_property : goal.subType,
  );

  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const { data: childrenGoal } = useGetFamilyGoal(analysisId, versionId, goal.groupGuid ?? "", {
    query: { enabled: goalType === GoalOverviewType.children },
  });
  const { data: vehicleGoal } = useGetVehicleGoal(analysisId, versionId, goal.guid, {
    query: { enabled: goalType === GoalOverviewType.vehicle },
  });
  const { data: pensionGoal } = useGetPensionGoal(analysisId, versionId, goal.guid, {
    query: { enabled: goalType === GoalOverviewType.pension },
  });
  const { data: financialReserveGoal } = useGetFinancialReserveGoal(analysisId, versionId, goal.guid, {
    query: { enabled: goalType === GoalOverviewType.financial_reserve },
  });
  const { data: otherGoal } = useGetOtherGoal(analysisId, versionId, goal.guid, {
    query: { enabled: goalType === GoalOverviewType.other },
  });
  const { data: housingOtherGoal } = useGetHousingOtherGoal(analysisId, versionId, goal.guid, {
    query: { enabled: goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_other },
  });
  const { data: housingMortgageGoal } = useGetHousingMortgageGoal(analysisId, versionId, goal.guid, {
    query: { enabled: goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_mortgage },
  });
  const { data: housingPropertyGoal } = useGetHousingPropertyGoal(analysisId, versionId, goal.guid, {
    query: { enabled: goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_property },
  });

  const [drawType, setDrawType] = useState(
    otherGoal && otherGoal.data.payoutFrequency === OtherGoalDataPayoutFrequency.regular ? DrawType.Monthly : DrawType.LumpSum,
  );

  const vehicleGoalFormRef = useRef<SubmitGoalFormHandle>(null);
  const emergencyFundFormRef = useRef<SubmitGoalFormHandle>(null);
  const otherGoalFormRef = useRef<SubmitGoalFormHandle>(null);
  const housingOtherFormRef = useRef<SubmitGoalFormHandle>(null);
  const housingMortgageFormRef = useRef<SubmitGoalFormHandle>(null);
  const housingPropertyFormRef = useRef<SubmitGoalFormHandle>(null);
  const pensionFormRef = useRef<SubmitGoalFormHandle>(null);
  const childrenGoalFormRef = useRef<SubmitGoalFormHandle>(null);

  const onDrawTypeChange = (drawType: DrawType) => {
    setDrawType(drawType);
  };

  const onSubTypeChange = (goalSubType: GoalOverviewSubType) => {
    setGoalSubType(goalSubType);
  };

  const onClose = () => {
    propsOnClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onExited={onExited}
      closeLabel={t(Resources.Common.Close)}
      title={t(Resources.AnalysisDetail.Goals.EditGoal)}
      primaryAction={
        <Button
          variant="contained"
          disabled={readOnly}
          type="submit"
          onClick={() => {
            switch (goalType) {
              case GoalOverviewType.children:
                childrenGoalFormRef.current?.submitForm();
                break;
              case GoalOverviewType.vehicle:
                vehicleGoalFormRef.current?.submitForm();
                break;
              case GoalOverviewType.financial_reserve:
                emergencyFundFormRef.current?.submitForm();
                break;
              case GoalOverviewType.other:
                if (drawType === DrawType.LumpSum) {
                  otherGoalFormRef.current?.submitForm();
                } else if (drawType === DrawType.Monthly) {
                  otherGoalFormRef.current?.submitForm();
                }
                break;
              case GoalOverviewType.housing:
                switch (goalSubType) {
                  case GoalOverviewSubType.housing_other:
                    housingOtherFormRef.current?.submitForm();
                    break;
                  case GoalOverviewSubType.housing_mortgage:
                    housingMortgageFormRef.current?.submitForm();
                    break;
                  case GoalOverviewSubType.housing_property:
                    housingPropertyFormRef.current?.submitForm();
                    break;
                }
                break;
              case GoalOverviewType.pension:
                pensionFormRef.current?.submitForm();
                break;
            }
          }}
        >
          {t(Resources.Common.Save)}
        </Button>
      }
    >
      <Box>
        <Box mb={Spacing.XXL}>
          <Stack spacing={Spacing.XL}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Title)}
            </Typography>
            <RadioGroup
              aria-label={t(Resources.AnalysisDetail.Goals.GoalType.Title)}
              value={goalType}
              onChange={(_, value) => {
                setGoalType(value as GoalOverviewType);
              }}
              sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: Spacing.M }}
            >
              {(Object.keys(GoalOverviewType) as Array<GoalOverviewType>).map((value) => (
                <GoalTile key={value} goalType={value} disabled={goalType !== value} />
              ))}
            </RadioGroup>
          </Stack>
        </Box>
        <Box mb={Spacing.XXL}>
          {goalType === GoalOverviewType.children && childrenGoal && (
            <ChildrenGoalPane onClose={onClose} ref={childrenGoalFormRef} editGoal={childrenGoal} />
          )}
          {goalType === GoalOverviewType.pension && pensionGoal && (
            <PensionGoalForm onClose={onClose} ref={pensionFormRef} editGoal={pensionGoal} goalId={goal.guid} />
          )}
          {goalType === GoalOverviewType.vehicle && vehicleGoal && (
            <VehicleGoalForm onClose={onClose} ref={vehicleGoalFormRef} editGoal={vehicleGoal} goalId={goal.guid} />
          )}
          {goalType === GoalOverviewType.financial_reserve && financialReserveGoal && (
            <FinancialReserveForm onClose={onClose} ref={emergencyFundFormRef} editGoal={financialReserveGoal} goalId={goal.guid} />
          )}
          {goalType === GoalOverviewType.other && goal.payoutFrequency === GoalOverviewPayoutFrequency.one_time && otherGoal && (
            <OtherGoalOneTimeForm
              onClose={onClose}
              onDrawTypeChange={onDrawTypeChange}
              ref={otherGoalFormRef}
              editGoal={otherGoal}
              goalId={goal.guid}
            />
          )}
          {goalType === GoalOverviewType.other && goal.payoutFrequency === GoalOverviewPayoutFrequency.regular && otherGoal && (
            <OtherGoalMonthlyForm
              onClose={onClose}
              onDrawTypeChange={onDrawTypeChange}
              ref={otherGoalFormRef}
              editGoal={otherGoal}
              goalId={goal.guid}
            />
          )}
          {goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_property && housingPropertyGoal && (
            <HousingPropertyGoalForm
              onSubTypeChange={onSubTypeChange}
              onClose={onClose}
              ref={housingPropertyFormRef}
              editGoal={housingPropertyGoal}
              goalId={goal.guid}
            />
          )}
          {goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_mortgage && housingMortgageGoal && (
            <HousingMortgageGoalForm
              onSubTypeChange={onSubTypeChange}
              onClose={onClose}
              ref={housingMortgageFormRef}
              editGoal={housingMortgageGoal}
              goalId={goal.guid}
            />
          )}
          {goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_other && housingOtherGoal && (
            <HousingOtherGoalForm
              onSubTypeChange={onSubTypeChange}
              onClose={onClose}
              ref={housingOtherFormRef}
              editGoal={housingOtherGoal}
              goalId={goal.guid}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
